import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Badge,
  useColorModeValue,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  Flex,
  Icon,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { FiClock, FiUsers, FiTrendingUp } from 'react-icons/fi';
import MessageApi from '../../../../api/message';
import { getCompanies } from '../../../../functions/companiesCache';

const EngagementReportModal = ({ isOpen, onClose, messageId }) => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);
  const [companiesCache, setCompaniesCache] = useState({});

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const summaryBgColor = useColorModeValue('blue.50', 'blue.900');
  const chartBgColor = useColorModeValue('gray.50', 'gray.700');
  const chartTextColor = useColorModeValue('gray.800', 'white');
  const gridStroke = useColorModeValue('#e2e8f0', '#4a5568');
  const axisTextColor = useColorModeValue('#4a5568', '#a0aec0');

  useEffect(() => {
    if (isOpen && messageId) {
      fetchEngagementReport();
      loadCompaniesCache();
    }
  }, [isOpen, messageId]);

  const loadCompaniesCache = async () => {
    try {
      const cache = await getCompanies();
      setCompaniesCache(cache);
    } catch (error) {
      console.error('Error loading companies cache:', error);
    }
  };

  const fetchEngagementReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await MessageApi.generateEngagementReport(messageId);
      if (response.data) {
        setReportData(response.data);
      }
    } catch (err) {
      console.error('Error fetching engagement report:', err);
      setError('שגיאה בטעינת דוח המעורבות. אנא נסה שוב.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeRangeLabel = (minutes) => {
    if (minutes === 30) return '30 דקות';
    if (minutes === 60) return 'שעה';
    if (minutes === 180) return '3 שעות';
    if (minutes === 720) return '12 שעות';
    return `${minutes} דקות`;
  };

  const calculateEngagementRate = (count, total) => {
    if (total === 0) return 0;
    return Math.round((count / total) * 100);
  };

  const parseMessageContent = (messageBody) => {
    try {
      const parsed = JSON.parse(messageBody);
      return {
        title: parsed.title || '',
        body: parsed.body || '',
        companiesId: parsed.companiesId || []
      };
    } catch (error) {
      // If parsing fails, treat as plain text
      return {
        title: '',
        body: messageBody || '',
        companiesId: []
      };
    }
  };

  const getCompanyNames = (companyIds) => {
    if (!companyIds || companyIds.length === 0) return 'לא צוין';

    const names = companyIds
      .map(id => companiesCache[id] || `חברה ${id}`)
      .filter(Boolean);

    return names.length > 0 ? names : ['לא נמצאו חברות'];
  };

  const renderCompanyNames = (companyIds) => {
    const names = getCompanyNames(companyIds);

    if (names.length === 1) {
      return <Text fontSize="md" color="gray.600" mt={1}>{names[0]}</Text>;
    }

    if (names.length <= 5) {
      return (
        <VStack align="start" spacing={1} mt={1}>
          {names.map((name, index) => (
            <Badge key={index} colorScheme="blue" variant="subtle" fontSize="xs">
              {name}
            </Badge>
          ))}
        </VStack>
      );
    }

    return (
      <VStack align="start" spacing={2} mt={1}>
        <HStack wrap="wrap" spacing={1}>
          {names.slice(0, 4).map((name, index) => (
            <Badge key={index} colorScheme="blue" variant="subtle" fontSize="xs">
              {name}
            </Badge>
          ))}
          <Badge colorScheme="gray" variant="subtle" fontSize="xs">
            +{names.length - 4} נוספות
          </Badge>
        </HStack>
        <Text fontSize="xs" color="gray.500">
          סה"כ {names.length} חברות
        </Text>
      </VStack>
    );
  };

  const formatHour = (hour) => {
    return hour.toString();
  };

  const formatAnalysisDate = (dateString) => {
    if (!dateString) return '';
    // Convert YYYYMMDD to readable format
    const year = dateString.substring(0, 4);
    const month = dateString.substring(4, 6);
    const day = dateString.substring(6, 8);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const prepare24HourChartData = (hourlyData) => {
    if (!hourlyData || hourlyData.length === 0) return [];

    // Create array for all 24 hours
    const chartData = [];
    for (let hour = 0; hour < 24; hour++) {
      const hourData = hourlyData.find(h => h.hour === hour);
      chartData.push({
        hour: formatHour(hour),
        hourNumber: hour,
        'פתיחות אפליקציה': hourData ? hourData.appOpens : 0,
        'העברות כספיות': hourData ? hourData.transfers : 0,
        'לחיצות על התראות': hourData ? hourData.notificationClicks : 0,
        'התראות שהתקבלו': hourData ? hourData.notificationReceived : 0,
      });
    }
    return chartData;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box bg="white" p={3} border="1px" borderColor="gray.200" borderRadius="md" shadow="md">
          <Text fontWeight="bold" mb={2}>{`שעה: ${label}`}</Text>
          {payload.map((entry, index) => (
            <Text key={index} color={entry.color}>
              {`${entry.dataKey}: ${entry.value}`}
            </Text>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent bg={bgColor} maxH="90vh">
        <ModalHeader>דוח מעורבות - הודעת Push</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          {loading ? (
            <VStack spacing={4} py={8}>
              <Spinner size="lg" />
              <Text>טוען דוח מעורבות...</Text>
            </VStack>
          ) : error ? (
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>שגיאה!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : reportData ? (
            <VStack spacing={6} align="stretch">
              {/* Message Info */}
              <Box p={4} border="1px" borderColor={borderColor} borderRadius="md">
                <VStack align="stretch" spacing={4}>
                  <Text fontSize="lg" fontWeight="bold">פרטי ההודעה</Text>

                  {(() => {
                    const messageContent = parseMessageContent(reportData.messageBody);
                    return (
                      <>
                        {messageContent.title && (
                          <Box>
                            <Text fontSize="md" fontWeight="semibold" color="gray.700">
                              כותרת:
                            </Text>
                            <Text fontSize="md" color="gray.600" mt={1}>
                              {messageContent.title}
                            </Text>
                          </Box>
                        )}

                        <Box>
                          <Text fontSize="md" fontWeight="semibold" color="gray.700">
                            הודעה:
                          </Text>
                          <Text fontSize="md" color="gray.600" mt={1} whiteSpace="pre-wrap">
                            {messageContent.body}
                          </Text>
                        </Box>

                        <Box>
                          <Text fontSize="md" fontWeight="semibold" color="gray.700">
                            נשלח לחברות:
                          </Text>
                          {renderCompanyNames(messageContent.companiesId)}
                        </Box>
                      </>
                    );
                  })()}

                  <Divider />

                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        תאריך שליחה:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {formatDateTime(reportData.sentAt)}
                      </Text>
                    </VStack>

                    <VStack align="end" spacing={1}>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        סה"כ נמענים:
                      </Text>
                      <Badge colorScheme="blue" fontSize="sm">
                        {reportData.totalAudience} משתמשים
                      </Badge>
                    </VStack>
                  </HStack>
                </VStack>
              </Box>

              <Divider />

              {/* Tabbed Content */}
              <Tabs variant="enclosed" colorScheme="blue">
                <TabList>
                  <Tab>
                    <Icon as={FiTrendingUp} mr={2} />
                    סקירה יומית (24 שעות)
                  </Tab>
                  <Tab>
                    <Icon as={FiClock} mr={2} />
                    טווחי זמן קצרים
                  </Tab>
                </TabList>

                <TabPanels>
                  {/* 24-Hour Overview Tab */}
                  <TabPanel>
                    <VStack spacing={6} align="stretch">
                      {/* 24-Hour Summary Stats */}
                      {reportData.hourlyData && (
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                          <Stat p={4} border="1px" borderColor={borderColor} borderRadius="md">
                            <StatLabel>
                              <Flex align="center">
                                <Icon as={FiUsers} mr={2} />
                                סה"כ פתיחות (24 שעות)
                              </Flex>
                            </StatLabel>
                            <StatNumber color="blue.500">
                              {reportData.totalAppOpens24h || 0}
                            </StatNumber>
                            <StatHelpText>
                              {calculateEngagementRate(reportData.totalAppOpens24h || 0, reportData.totalAudience)}% מהקהל
                            </StatHelpText>
                          </Stat>

                          <Stat p={4} border="1px" borderColor={borderColor} borderRadius="md">
                            <StatLabel>
                              <Flex align="center">
                                <Icon as={FiTrendingUp} mr={2} />
                                סה"כ העברות (24 שעות)
                              </Flex>
                            </StatLabel>
                            <StatNumber color="green.500">
                              {reportData.totalTransfers24h || 0}
                            </StatNumber>
                            <StatHelpText>
                              {calculateEngagementRate(reportData.totalTransfers24h || 0, reportData.totalAudience)}% מהקהל
                            </StatHelpText>
                          </Stat>

                          <Stat p={4} border="1px" borderColor={borderColor} borderRadius="md">
                            <StatLabel>
                              <Flex align="center">
                                <Icon as={FiClock} mr={2} />
                                סה"כ לחיצות התראות (24 שעות)
                              </Flex>
                            </StatLabel>
                            <StatNumber color="red.500">
                              {reportData.totalNotificationClicks24h || 0}
                            </StatNumber>
                            <StatHelpText>
                              {calculateEngagementRate(reportData.totalNotificationClicks24h || 0, reportData.totalAudience)}% מהקהל
                            </StatHelpText>
                          </Stat>

                          <Stat p={4} border="1px" borderColor={borderColor} borderRadius="md">
                            <StatLabel>
                              <Flex align="center">
                                <Icon as={FiClock} mr={2} />
                                סה"כ התראות שהתקבלו (24 שעות)
                              </Flex>
                            </StatLabel>
                            <StatNumber color="purple.500">
                              {reportData.totalNotificationReceived24h || 0}
                            </StatNumber>
                            <StatHelpText>
                              {calculateEngagementRate(reportData.totalNotificationReceived24h || 0, reportData.totalAudience)}% מהקהל
                            </StatHelpText>
                          </Stat>

                          <Stat p={4} border="1px" borderColor={borderColor} borderRadius="md">
                            <StatLabel>תאריך הניתוח</StatLabel>
                            <StatNumber fontSize="lg">
                              {formatAnalysisDate(reportData.analysisDate)}
                            </StatNumber>
                            <StatHelpText>נתונים מ-Google Analytics</StatHelpText>
                          </Stat>
                        </SimpleGrid>
                      )}

                      {/* 24-Hour Chart */}
                      {reportData.hourlyData && reportData.hourlyData.length > 0 ? (
                        <Box p={6} border="1px" borderColor={borderColor} borderRadius="lg" bg={chartBgColor}>
                          <VStack spacing={4}>
                            <Text fontSize="xl" fontWeight="bold" textAlign="center" color={chartTextColor}>
                              התפלגות פעילות לפי שעות (24 שעות)
                            </Text>
                            <Text fontSize="sm" color="gray.500" textAlign="center">
                              גרף מציג את מספר פתיחות האפליקציה, העברות כספיות, לחיצות על התראות והתראות שהתקבלו לכל שעה ביום
                            </Text>
                            <Box height="500px" width="100%">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                  data={prepare24HourChartData(reportData.hourlyData)}
                                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                                  barCategoryGap="10%"
                                >
                                  <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                                  <XAxis
                                    dataKey="hour"
                                    tick={{ fontSize: 12, fill: axisTextColor }}
                                    interval={0}
                                    height={60}
                                  />
                                  <YAxis
                                    tick={{ fontSize: 12, fill: axisTextColor }}
                                    label={{
                                      value: 'מספר משתמשים',
                                      angle: -90,
                                      position: 'outside',
                                      offset: 10,
                                      style: { textAnchor: 'middle' }
                                    }}
                                    width={60}
                                  />
                                  <Tooltip content={<CustomTooltip />} />
                                  <Legend
                                    wrapperStyle={{ paddingTop: '20px' }}
                                    iconType="rect"
                                  />
                                  <Bar
                                    dataKey="פתיחות אפליקציה"
                                    fill="#3182CE"
                                    name="פתיחות אפליקציה"
                                    radius={[3, 3, 0, 0]}
                                  />
                                  <Bar
                                    dataKey="העברות כספיות"
                                    fill="#38A169"
                                    name="העברות כספיות"
                                    radius={[3, 3, 0, 0]}
                                  />
                                  <Bar
                                    dataKey="לחיצות על התראות"
                                    fill="#E53E3E"
                                    name="לחיצות על התראות"
                                    radius={[3, 3, 0, 0]}
                                  />
                                  <Bar
                                    dataKey="התראות שהתקבלו"
                                    fill="#805AD5"
                                    name="התראות שהתקבלו"
                                    radius={[3, 3, 0, 0]}
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                          </VStack>
                        </Box>
                      ) : (
                        <Box p={6} border="1px" borderColor={borderColor} borderRadius="lg" textAlign="center">
                          <Text color="gray.500">אין נתונים זמינים לתצוגת 24 שעות</Text>
                        </Box>
                      )}
                    </VStack>
                  </TabPanel>

                  {/* Time Ranges Tab */}
                  <TabPanel>
                    <VStack spacing={6} align="stretch">
                      <Text fontSize="lg" fontWeight="bold" textAlign="center">
                        נתוני מעורבות לפי טווחי זמן קצרים
                      </Text>
                      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                        {reportData.timeRanges.map((timeRange, index) => (
                          <GridItem key={index}>
                            <Box p={4} border="1px" borderColor={borderColor} borderRadius="md">
                              <VStack align="stretch" spacing={3}>
                                <Text fontSize="md" fontWeight="bold" textAlign="center">
                                  {getTimeRangeLabel(timeRange.timeRangeMinutes)}
                                </Text>

                                <Stat>
                                  <StatLabel>פתיחות אפליקציה</StatLabel>
                                  <StatNumber>{timeRange.appOpens}</StatNumber>
                                  <StatHelpText>
                                    {calculateEngagementRate(timeRange.appOpens, reportData.totalAudience)}% מהקהל
                                  </StatHelpText>
                                </Stat>

                                <Stat>
                                  <StatLabel>העברות כספיות</StatLabel>
                                  <StatNumber>{timeRange.transfers}</StatNumber>
                                  <StatHelpText>
                                    {calculateEngagementRate(timeRange.transfers, reportData.totalAudience)}% מהקהל
                                  </StatHelpText>
                                </Stat>

                                <Stat>
                                  <StatLabel>לחיצות על התראות</StatLabel>
                                  <StatNumber>{timeRange.notificationClicks}</StatNumber>
                                  <StatHelpText>
                                    {calculateEngagementRate(timeRange.notificationClicks, reportData.totalAudience)}% מהקהל
                                  </StatHelpText>
                                </Stat>
                              </VStack>
                            </Box>
                          </GridItem>
                        ))}
                      </Grid>

                      {/* Summary for Time Ranges */}
                      <Box p={4} bg={summaryBgColor} borderRadius="md">
                        <Text fontSize="md" fontWeight="bold" mb={2}>סיכום טווחי זמן</Text>
                        <VStack align="stretch" spacing={2}>
                          <Text fontSize="sm">
                            • הודעה זו נשלחה ל-{reportData.totalAudience} משתמשים
                          </Text>
                          <Text fontSize="sm">
                            • שיעור הפתיחה הגבוה ביותר: {Math.max(...reportData.timeRanges.map(tr => calculateEngagementRate(tr.appOpens, reportData.totalAudience)))}% (ב-30 הדקות הראשונות)
                          </Text>
                          <Text fontSize="sm">
                            • סה"כ העברות כספיות בטווחים: {reportData.timeRanges.reduce((sum, tr) => sum + tr.transfers, 0)}
                          </Text>
                          <Text fontSize="sm">
                            • סה"כ לחיצות על התראות בטווחים: {reportData.timeRanges.reduce((sum, tr) => sum + tr.notificationClicks, 0)}
                          </Text>
                        </VStack>
                      </Box>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          ) : null}
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose}>סגור</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EngagementReportModal;
